import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, switchMap, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';

export interface DeckLoginRequest {
  username: string;
  password: string;
}

export interface DeckUser {
  id: string;
  uuid: string;
  name: string;
  username: string;
  email: string;
}

export interface DeckLoginResponse {
  message: string;
  details: {
    token: string;
    token_type: string;
    user: DeckUser;
  };
}

export interface RegisterDeckRequest {
  qrCode: string;
}

export interface RegisterDeckResponse {
  message: string;
}

export interface DeckEntry {
  deckId: number;
  deckName: string;
  prodType: string;
  countryCode: string;
  prodNo: string;
  prodYear: string;
  prodBatch: string;
  boxSerial: string;
  status: string;
  registered_at: string;
  id?: string;
}

export interface CardEntry {
  card_id: string;
  name: string;
  shape: string;
  class: string;
  primary_color: string;
  secondary_color: string;
  category: string;
  level: number;
  abilities: string[];
  special_effects: string | null;
  card_web_image: string;
  type: string;
  trigger: string;
  tribe: string;
  status: string;
}

@Injectable({ providedIn: 'root' })
export class DeckService {
  private apiUrl = environment.apiUrl;

  private userSubject = new BehaviorSubject<DeckUser | null>(null);
  user$ = this.userSubject.asObservable();

  private myDecksSubject = new BehaviorSubject<DeckEntry[] | null>(null);
  myDecks$ = this.myDecksSubject.asObservable();

  lastRegisteredDeck: RegisterDeckResponse | null = null;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService
  ) {
    const stored = sessionStorage.getItem(environment.storageKey);
    if (stored) {
      try {
        const user = JSON.parse(stored);
        // Fix for missing uuid in existing sessions
        if (user && !user.uuid && user.id) {
          console.log('Restoring session: fixing missing uuid from id');
          user.uuid = user.id;
        }
        this.userSubject.next(user);
      } catch (e) {
        console.error('Error parsing stored session', e);
      }
    }
  }

  login(data: DeckLoginRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}api/v1/auth/login`, data, { withCredentials: true }).pipe(
      switchMap(() => this.getUserDetails()),
      tap(res => {
        console.log('User details response:', res);
        const details = res.details || res.user || res;
        if (details) {
          const user: DeckUser = {
            id: (details.userId || details.id)?.toString(),
            uuid: details.uuid,
            name: details.name,
            username: details.username,
            email: details.email
          };
          console.log('Mapped user object:', user);
          sessionStorage.setItem(environment.storageKey, JSON.stringify(user));
          this.userSubject.next(user);
        } else {
          console.error('No details found in user details response');
        }
      })
    );
  }

  logout(): void {
    this.http.post(`${this.apiUrl}api/v1/user/logout`, {}, { withCredentials: true }).subscribe();
    this.cookieService.delete('token', '/');
    sessionStorage.removeItem(environment.storageKey);
    this.userSubject.next(null);
    this.myDecksSubject.next(null);
  }

  isLoggedIn(): boolean {
    return !!sessionStorage.getItem(environment.storageKey);
  }

  getUser(): DeckUser | null {
    return this.userSubject.value;
  }

  updateUser(user: DeckUser): void {
    this.userSubject.next(user);
  }

  registerDeck(data: RegisterDeckRequest): Observable<RegisterDeckResponse> {
    return this.http.post<RegisterDeckResponse>(
      `${this.apiUrl}api/v1/deck/register`,
      data
    ).pipe(
      tap(res => {
        this.lastRegisteredDeck = res;
        this.myDecksSubject.next(null); // Invalidate cache on success
      })
    );
  }

  getUserDetails(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}api/v1/user/details`, { withCredentials: true });
  }

  getMyDecks(forceRefresh = false): Observable<DeckEntry[]> {
    if (this.myDecksSubject.value && !forceRefresh) {
      return of(this.myDecksSubject.value);
    }

    return this.http.get<any>(`${this.apiUrl}api/v1/deck/my-decks`, { withCredentials: true }).pipe(
      map(res => {
        const decks = Array.isArray(res) ? res : (res?.details || res?.data || []);
        this.myDecksSubject.next(decks);
        return decks;
      })
    );
  }

  getDeckCards(deckId: number): Observable<CardEntry[]> {
    return this.http.get<any>(`${this.apiUrl}api/v1/deck/${deckId}/cards`, { withCredentials: true }).pipe(
      map(res => {
        return Array.isArray(res) ? res : (res?.details || res?.data || []);
      })
    );
  }
}
